import { useEffect, useState, useContext, useCallback, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import {
  pipe,
  pathOr,
  propEq,
  find,
  isEmpty,
  reduce,
  values,
  isNil,
  filter,
  complement,
  prop,
  path,
  __
} from 'ramda'
import { camelCase } from 'lodash'
import { select } from '@/store'
import { colors, fontWeights } from '@/styles'
import useIsMobile from '@/hooks/use-is-mobile'
import { Button } from '@/components/button'
import SignersWithItems from './signer-with-items-component'
import InitialFieldIcon from './initial-field-icon.svg?react'
import SignatureFieldIcon from './signature-field-icon.svg?react'
import DateFieldIcon from './date-field-icon.svg?react'
import DuplicateIcon from './duplicate-icon.svg?react'
import DeleteIcon from './delete-icon.svg?react'
import TextSize from './text-size.svg?react'
import RecipientsIcon from './recipients-icon.svg?react'
import TextIcon from './text-icon.svg?react'
import DateIcon from './date-icon.svg?react'
import SignatureIcon from './signature-icon.svg?react'
import InitialsIcon from './initials-icon.svg?react'
import DropdownComponent from '../dropdown-component'
import { Dropdown, DropdownItem } from '../dropdown'
import Context from '../context'
import { getTextWidth } from '../text-width'
import inputTextSizes from '../text-sizes'
import { updateAt } from '../utils'
import CogIcon from '../dropdown/cog-icon.svg'
import { show as showManageRecipientsModal } from '../add-signer/manage-recipients-modal'

const DocumentSettings = styled.div`
  background-color: ${colors.white};
  width: 100%;
  ${props => (props.isMobile ? 'max-width: 100%;' : 'max-width: 240px;')}
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
`

const TitleWrapper = styled.div`
  display: flex;
  margin: 12px 0;
`

const ShapeTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 12px 0;
  > :first-child {
    margin-right: 5px;
  }
`

const Title = styled.h5`
  font-size: 16px;
  font-weight: 700;
`

const EmptyUsersText = styled.p`
  font-weight: ${fontWeights.regular};
  font-size: 14px;
  line-height: 20px;
  color: ${colors.red64};
  margin: 18px 0 0 0;
`

const SubTitle = styled.h5`
  font-size: 14px;
  line-height: 16px;
  font-weight: ${fontWeights.regular};
  margin-bottom: 12px;
`

const DraggableFields = styled.div`
  position: relative;
  width: 100%;
  max-width: 200px;
  height: 48px;
  background: rgba(${props => props.opacity}, 0.1);
  color: ${props => props.color};
  border: 1.5px dashed ${props => props.color};
  border-left: 1.5px solid ${props => props.color};
  margin-bottom: 12px;
  padding: 14px;
  cursor: pointer;

  ${props =>
    !props.disabled &&
    `
    &:hover {
      padding-left: 36px;
      :before {
        content: '\\283F';
        position: absolute;
        width: 24px;
        height: 33px;
        left: -1px;
        top: -1px;
        padding-top: 12px;
        text-align: center;
        border: 1px solid ${props => props.color};
        background: rgba(${props => props.opacity}, 0.2);
      }
    }
  `}
  ${props =>
    props.disabled &&
    `
      opacity: 0.5;
      cursor: not-allowed;
  `}
`

const DraggableComponentText = styled.p`
  display: inline;
  margin: 0;
  margin-left: 10px;
  font-size: 14px;
`

const DraggableComponentControlWrapper = styled.div`
  width: 100%;
  max-width: 200px;
  height: 48px;
  padding: 14px;
  border-radius: 8px;
  color: ${colors.grey56};
  cursor: pointer;

  ${props => (props.disabled === true ? 'pointer-events: none;' : '')}

  &:hover {
    background-color: ${colors.grey96};
  }

  &:active {
    background-color: ${colors.grey88};
    color: ${colors.black};
  }
`

const DraggableComponentControlText = styled.p`
  display: inline;
  margin: 0;
  margin-left: 10px;
  font-size: 14px;
`

const DropdownWrapper = styled.div``

const TextSizeWidgetWrapper = styled.div`
  display: flex;
`

const CreateContractWrapper = styled.div``

const Hr = styled.hr`
  border: 1px solid ${colors.grey88};
  margin: 10px 0;
`

const WidgetTextAreaWrapper = styled.div`
  margin: 16px 0;
`

const WidgetTextAreaInnerWrapper = styled.div`
  margin-bottom: 16px;
  border-radius: 6px;
  border: 1px solid ${colors.grey};
`

const WidgetTextArea = styled.textarea`
  width: 100%;
  height: 100px;
  padding: 10px;
  border: none;
  border-radius: 6px;
  text-align: left;
  resize: none;
`

const WidgetTextAreaCounter = styled.div`
  position: relative;
  font-size: 12px;
  text-align: end;
  right: 10px;
`

const ButtonWrapper = styled.div`
  padding: 8px;
  > button {
    width: 100%;
    max-height: 32px;
    min-height: 32px;
  }
`

const AddSignerImg = styled.img`
  width: 14px;
`

const TextSizeWidget = ({ inputTextSize }) => {
  return (
    <TextSizeWidgetWrapper>
      <TextSize fill="#000000" style={{ marginRight: '6px' }} alt="" /> {inputTextSize}
    </TextSizeWidgetWrapper>
  )
}

const hexToRgb = hex => {
  // 99, 99, 99 is a fallback
  if (!hex) return '99, 99, 99'
  return hex
    .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b)
    .substring(1)
    .match(/.{2}/g)
    .map(x => parseInt(x, 16))
}

const DraggableComponent = ({
  color,
  opacity,
  text,
  icon,
  id,
  inputType,
  recipient_id,
  disabled,
  currentDocument,
  items,
  setItems,
  stageRef,
  stageWidth,
  stageHeight
}) => {
  const data = {
    color: color,
    opacity: opacity,
    text: text,
    id: id,
    inputType: inputType,
    recipient_id: recipient_id
  }

  const handleClick = () => {
    if (disabled) return
    const stage = stageRef?.current?.getStage()
    const layer = stage?.find('Layer')
    let currentPageName

    layer[0].find('Image')?.forEach(item => {
      if (item.visible() === true) {
        currentPageName = item.id()
      }
    })

    const oldItems = path([currentDocument], items) ?? []

    const height = inputType === 'text' ? 16 : 48
    const x = stageWidth / 2 - 100
    const y = (stageHeight - height) / 2

    const updatedItems = updateAt(
      items,
      [currentDocument],
      [
        ...oldItems,
        {
          x: x,
          y: y,
          width: 200,
          height: height,
          stroke: color,
          name: currentPageName,
          id: `${id}_${camelCase(text)}_${Math.floor(Math.random() * 10000)}`,
          recipient_id: recipient_id,
          text: text,
          inputType: inputType,
          textSize: 16
        }
      ]
    )

    setItems(updatedItems)
  }
  return (
    <DraggableFields
      draggable={!disabled}
      color={color}
      opacity={opacity}
      onDragStart={e => {
        e.dataTransfer.setData('text', JSON.stringify(data))
        e.dataTransfer.setDragImage(e.target, 0, 0)
      }}
      disabled={disabled}
      onClick={handleClick}
    >
      {icon}
      <DraggableComponentText>{text}</DraggableComponentText>
    </DraggableFields>
  )
}

const DocumentWidget = ({
  selectedPage,
  stageRef,
  currentPage,
  handleDuplicate,
  handleDelete,
  selectedShape,
  signees,
  currentDocument,
  contractId,
  setSignees,
  stageWidth,
  stageHeight,
  isSigning,
  selectShape,
  parentRef
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const dispatch = useDispatch()
  const userContext = useContext(Context)
  const stage = stageRef?.current?.getStage()
  const layer = stage?.find('Layer')

  const [itemsLength, setItemsLength] = useState(0)

  const {
    items,
    setItems,
    setFieldsCounter,
    fileMetadata,
    setFileMetadata,
    user: currentUser,
    setUser
  } = userContext ?? {}

  const { recipients = {} } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId })
  )

  const [inputTextValueTextArea, setInputTextValueTextArea] = useState('')
  const [inputTextSizeTextArea, setInputTextSizeTextArea] = useState('')
  const [selectedUserData, setSelectedUserData] = useState('')

  const currentSignees = signees?.[currentDocument] ?? {}

  const filteredSignees = pipe(
    pathOr({}, [currentDocument]),
    values,
    filter(propEq('recipient_signing_status', 'signature_required'))
  )(signees)
  const noSigners = isEmpty(filteredSignees)

  const signingUsers = pipe(
    pathOr({}, [currentDocument]),
    values,
    filter(propEq('recipient_signing_status', 'signature_required'))
  )(signees)

  const hasSelectedUser = pipe(
    filter(propEq('selected', true)),
    complement(isEmpty)
  )(filteredSignees)

  const currentItems = pathOr([], [currentDocument], items)
  const currentItem = currentItems.filter(obj => obj.id === selectedShape)[0]

  const textType = currentItem?.inputType === 'text'
  const hasShape = !isNil(selectedShape)
  const ItemsWithoutTextFields = currentItems.filter(obj => {
    return obj.inputType !== 'text' && obj.inputType !== 'date'
  })
  const currentDocumentId = fileMetadata?.current_document
  const textAreaMaxCount = 4000

  const handleCounter = useCallback(() => {
    if (typeof document === 'undefined') return
    if (formatFieldsData(layer)) {
      setFieldsCounter(
        Object.values(
          formatFieldsData(layer).reduce((a, { userId, color }) => {
            let key = `${userId}_${color}`
            a[key] = a[key] || { userId, color, count: 0 }
            a[key].count++
            return a
          }, {})
        )
      )
    }
  }, [layer, setFieldsCounter])

  useEffect(() => {
    async function fetchData() {
      await dispatch.contract.getRecipients({ contractId: contractId })
    }

    if (isEmpty(recipients)) fetchData()
  }, [])

  useEffect(() => {
    setInputTextValueTextArea(currentItem?.text)
    setInputTextSizeTextArea(currentItem?.textSize)
  }, [currentItem])

  useEffect(() => {
    if (currentItems.length > itemsLength) setItemsLength(currentItems.length)
  }, [currentItems.length, itemsLength])

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.addEventListener('drop', handleCounter, false)
    return () => document.removeEventListener('drop', handleCounter, false)
  }, [handleCounter])

  useEffect(() => {
    userContext.setUser(
      <DropdownComponent
        color={selectedUserData?.color}
        name={`${selectedUserData?.first_name} ${selectedUserData?.last_name}`}
        email={selectedUserData?.email}
        id={selectedUserData?.id}
        recipient_id={selectedUserData?.recipient_id}
        maxWidth={135}
      />
    )
    handleCounter()
    // Unsafe missing useEffect deps - TODO refactor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserData?.id])

  // Inital fields toggle
  useEffect(() => {
    if (typeof layer === 'undefined') return
    if (selectedPage === 0) {
      layer[0]?.find('Group').forEach(item => {
        if (item.name() !== 'page-0') item.hide()
        // Hack to display transformer box
        if (item.id() === '') item.show()
      })
    }
  }, [layer, selectedPage])

  useEffect(() => {
    if (selectedUserData?.id) return
    if (!hasSelectedUser) {
      const firstUser = filteredSignees?.[0]
      setSelectedUserData(firstUser)
      return
    }

    filteredSignees.forEach(signee => {
      const k = signee.id
      if (currentSignees[k].selected === true) {
        setSelectedUserData(signee)
      }
    })
  }, [noSigners])

  useEffect(() => {
    if (noSigners) {
      setSelectedUserData({})
      setUser(undefined)
    }
  }, [noSigners])

  const formatFieldsData = data => {
    if (data === undefined) return
    const newArray = []
    data[0]?.find('Group').forEach(item => {
      const id = item.id()
      if (id.includes('label')) {
        const parsedId = item.id().split('-')[0]
        const parsedData = {
          userId: parsedId,
          id: id,
          name: item.name(),
          x: item.x(),
          y: item.y(),
          width: item.width(),
          height: item.height(),
          color: item
            .find('Tag')
            .map(item => item.attrs.stroke)
            .join()
        }
        newArray.push(parsedData)
      }
    })

    return newArray
  }

  const handleInputTextChange = e => {
    setInputTextValueTextArea(e.target.value)
  }

  const handleBlurTextChange = e => {
    // 20 is the basic padding value of the stage
    const stageWidth = stageRef.current.width() - 20
    const newText = e.target.value
    const inputTextWidth = getTextWidth(newText, `bold ${currentItem.textSize}px arial`)

    const newItems = currentItems.map(item => {
      const [userId, __, randomId] = item.id.split('-')
      const newId = `${userId}-${camelCase(newText)}-${randomId}`
      if (item.id === currentItem.id) {
        return {
          ...item,
          id: newId,
          text: newText,
          width: inputTextWidth > stageWidth ? parseInt(stageWidth) : parseInt(inputTextWidth)
        }
      }
      return item
    })
    const updatedItems = updateAt(items, [currentDocumentId], newItems)
    setItems(updatedItems)
  }
  const handleDropdown = e => {
    if (isSigning) return
    const currentDropdown = parentRef.current.children[0].find(`#${selectedShape}`)
    if (!currentDropdown[0]) return

    const currentName = currentDropdown[0].attrs.name
    let textAttrs

    currentDropdown[0].find('Label').forEach(item => {
      textAttrs = item?.children[2]?.attrs
    })

    const newId = `${e.props.id}_${camelCase(textAttrs?.text)}_${Math.floor(Math.random() * 10000)}`
    const newItems = currentItems.map(item => {
      if (item.id === currentItem.id) {
        return {
          ...item,
          id: newId,
          name: currentName,
          stroke: e.props.color,
          recipient_id: e.props?.recipient_id
        }
      }
      return item
    })
    setItems(updateAt(items, [currentDocument], newItems))
    selectShape(newId)
    parentRef.current.draw()
  }
  const handleSizeTextChange = e => {
    setInputTextSizeTextArea(parseInt(e))
    const newItems = currentItems.map(item => {
      if (item.id === currentItem.id) {
        return {
          ...item,
          textSize: parseInt(e)
        }
      }
      return item
    })
    const updatedItems = updateAt(items, [currentDocumentId], newItems)
    setItems(updatedItems)
  }
  const shapeTexts = {
    initial: t('l.contract_edit.document_widget.initials.text', 'Initials'),
    signature: t('l.contract_edit.document_widget.signature.text', 'Signature'),
    date: t('l.contract_edit.document_widget.date_signed.text', 'Date Signed'),
    text: t('l.contract_edit.document_widget.text_field.text', 'Text Field')
  }

  const shapeIcons = {
    initial: <InitialsIcon />,
    signature: <SignatureIcon />,
    date: <DateIcon />,
    text: <TextIcon />
  }

  const selectedShapeText = shapeTexts?.[currentItem?.inputType]
  const selectedShapeIcon = shapeIcons?.[currentItem?.inputType]

  const groupedItems = pipe(
    reduce((acc, item) => {
      const recipient_id = item?.recipient_id
      const existingItems = acc[recipient_id] ?? 0
      return {
        ...acc,
        [recipient_id]: existingItems + 1
      }
    }, {})
  )(ItemsWithoutTextFields)

  const showEdit = (user, recipients) =>
    pipe(
      values,
      find(propEq('recipient_id', user?.recipient_id)),
      complement(path(['contract_documents', 0, 'is_creator']))
    )(recipients)
  const isApplicant = (user, recipients) =>
    pipe(values, find(propEq('recipient_id', user?.recipient_id)), prop('is_applicant'))(recipients)
  return (
    <DocumentSettings isMobile={isMobile}>
      {currentPage === 'createContract' && (
        <CreateContractWrapper>
          <ShapeTitleWrapper>
            <RecipientsIcon />
            <Title>{t('l.contract_edit.document_widget.recipients.title', 'Recipients')}</Title>
          </ShapeTitleWrapper>
          {isEmpty(signingUsers) ? (
            <EmptyUsersText>
              {t(
                'l.contract_edit.document_widget.No recipients with required signatures found..title',
                'No recipients with required signatures found.'
              )}
            </EmptyUsersText>
          ) : (
            signingUsers.map((signee, i) => {
              return (
                <Fragment key={i}>
                  <SignersWithItems
                    color={signee.color}
                    name={`${signee.first_name} ${signee.last_name}`}
                    email={signee.email}
                    itemCount={groupedItems?.[signee.recipient_id]}
                  />

                  {i !== signingUsers?.length - 1 && <Hr />}
                </Fragment>
              )
            })
          )}
        </CreateContractWrapper>
      )}
      {currentPage === 'buildDocuments' && (
        <>
          {noSigners && (
            <ButtonWrapper>
              <Button
                type="button"
                theme="secondary"
                size="small"
                onClick={() =>
                  showManageRecipientsModal({
                    contractId,
                    fileMetadata,
                    setFileMetadata,
                    signees,
                    setSignees,
                    currentUser,
                    setUser,
                    items,
                    setItems,
                    currentDocument
                  })
                }
              >
                <AddSignerImg src={CogIcon} alt="" />
                {t('l.contract_edit.dropdownmManage_recipients.button', 'Manage Recipients')}
              </Button>
              <EmptyUsersText>
                {t(
                  'l.contract_edit.document_widget.No recipients with required signatures found..title',
                  'No recipients with required signatures found.'
                )}
              </EmptyUsersText>
            </ButtonWrapper>
          )}
          {hasShape && (
            <ShapeTitleWrapper>
              {selectedShapeIcon}
              <Title>{selectedShapeText}</Title>
            </ShapeTitleWrapper>
          )}
          {!noSigners && (!hasShape || !textType) && (
            <DropdownWrapper>
              <Dropdown
                value="User Name"
                display={userContext.user}
                height="51"
                onChange={e => {
                  userContext.setUser(e)
                  handleDropdown(e)
                }}
                fileMetadata={fileMetadata}
                setFileMetadata={setFileMetadata}
                contractId={contractId}
                signees={signees}
                setSignees={setSignees}
                currentUser={currentUser}
                setUser={setUser}
                items={items}
                setItems={setItems}
                currentDocument={currentDocument}
              >
                {filteredSignees.map((signee, i) => {
                  const key = signee?.recipient_id
                  const user = recipients?.[key]

                  return (
                    <DropdownItem
                      key={`signee-${key}`}
                      value={
                        <DropdownComponent
                          color={signee.color}
                          name={`${signee.first_name} ${signee.last_name}`}
                          email={signee.email}
                          id={key}
                          recipient_id={signee.recipient_id}
                        />
                      }
                    >
                      <DropdownComponent
                        color={signee.color}
                        name={`${signee.first_name} ${signee.last_name}`}
                        email={signee.email}
                        recipient_id={signee.recipient_id}
                        id={key}
                        user={user}
                        fileMetadata={fileMetadata}
                        contractId={contractId}
                        maxWidth={160}
                        signees={signees}
                        setSignees={setSignees}
                        showEdit={showEdit(signee, recipients)}
                        disableEdit={isApplicant(signee, recipients)}
                        items={items}
                      />
                    </DropdownItem>
                  )
                })}
              </Dropdown>
            </DropdownWrapper>
          )}
          {!hasShape && (
            <>
              <TitleWrapper>
                <Title>
                  {t(
                    'l.contract_edit.document_widget.standard_user_fields.title',
                    'Standard User Fields'
                  )}
                </Title>
              </TitleWrapper>
              <SubTitle>
                {t('l.contract_edit.document_widget.signature.title', 'Signature')}
              </SubTitle>
              <DraggableComponent
                color={userContext?.user?.props?.color}
                opacity={hexToRgb(userContext?.user?.props?.color).toString()}
                text={t('l.contract_edit.document_widget.initials.text', 'Initials')}
                icon={<InitialFieldIcon fill={userContext?.user?.props?.color} />}
                id={userContext?.user?.props?.id}
                recipient_id={userContext?.user?.props?.recipient_id}
                inputType="initials"
                disabled={noSigners}
                layer={layer}
                currentDocument={currentDocument}
                items={items}
                setItems={setItems}
                stageRef={stageRef}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
              />
              <DraggableComponent
                color={userContext?.user?.props?.color}
                opacity={hexToRgb(userContext?.user?.props?.color).toString()}
                text={t('l.contract_edit.document_widget.signature.text', 'Signature')}
                icon={<SignatureFieldIcon fill={userContext?.user?.props?.color} />}
                id={userContext?.user?.props?.id}
                recipient_id={userContext?.user?.props?.recipient_id}
                inputType="signature"
                disabled={noSigners}
                layer={layer}
                currentDocument={currentDocument}
                items={items}
                setItems={setItems}
                stageRef={stageRef}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
              />
              <SubTitle>
                {t('l.contract_edit.document_widget.auto_fill.title', 'Auto-fill')}
              </SubTitle>
              <DraggableComponent
                color={userContext?.user?.props?.color}
                opacity={hexToRgb(userContext?.user?.props?.color).toString()}
                text={t('l.contract_edit.document_widget.date_signed.text', 'Date Signed')}
                icon={<DateFieldIcon fill={userContext?.user?.props?.color} />}
                id={userContext?.user?.props?.id}
                recipient_id={userContext?.user?.props?.recipient_id}
                inputType="dateSigned"
                disabled={noSigners}
                layer={layer}
                currentDocument={currentDocument}
                items={items}
                setItems={setItems}
                stageRef={stageRef}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
              />

              <TitleWrapper>
                <Title>
                  {t('l.contract_edit.document_widget.general_fields.title', 'General Fields')}
                </Title>
              </TitleWrapper>
              <DraggableComponent
                color={colors.grey40}
                opacity={hexToRgb().toString()}
                text={t('l.contract_edit.document_widget.text_field.text', 'Text')}
                icon={<SignatureFieldIcon fill={colors.grey40} />}
                id={userContext?.user?.props?.id}
                recipient_id={userContext?.user?.props?.recipient_id}
                inputType="text"
                layer={layer}
                currentDocument={currentDocument}
                items={items}
                setItems={setItems}
                stageRef={stageRef}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
              />
            </>
          )}
          {hasShape && (
            <>
              {textType && (
                <WidgetTextAreaWrapper>
                  <WidgetTextAreaInnerWrapper>
                    <WidgetTextArea
                      value={inputTextValueTextArea}
                      onChange={handleInputTextChange}
                      onBlur={handleBlurTextChange}
                      maxLength={textAreaMaxCount}
                    />
                    <WidgetTextAreaCounter>
                      {inputTextValueTextArea?.length} / {textAreaMaxCount}
                    </WidgetTextAreaCounter>
                  </WidgetTextAreaInnerWrapper>
                  <Dropdown
                    display={<TextSizeWidget inputTextSize={inputTextSizeTextArea} />}
                    isText={textType}
                    onChange={e => {
                      handleSizeTextChange(e)
                    }}
                  >
                    {inputTextSizes.map((item, i) => {
                      return (
                        <DropdownItem key={i} value={item}>
                          {item}
                        </DropdownItem>
                      )
                    })}
                  </Dropdown>
                </WidgetTextAreaWrapper>
              )}
              <DraggableComponentControlWrapper disabled={!selectedShape} onClick={handleDuplicate}>
                <DuplicateIcon fill={selectedShape ? colors.black : colors.grey56} />
                <DraggableComponentControlText>
                  {t('l.contract_edit.document_widget.duplicate.button', 'Duplicate')}
                </DraggableComponentControlText>
              </DraggableComponentControlWrapper>
              <DraggableComponentControlWrapper disabled={!selectedShape} onClick={handleDelete}>
                <DeleteIcon fill={selectedShape ? colors.red64 : colors.grey56} />
                <DraggableComponentControlText>
                  {t('common.delete', 'Delete')}
                </DraggableComponentControlText>
              </DraggableComponentControlWrapper>
            </>
          )}
        </>
      )}
    </DocumentSettings>
  )
}

export default DocumentWidget
